import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { Activity, Box as BoxIcon, Crown, LineChart, Sparkles, X } from 'lucide-react';

import { MOCK_LUMINAIRES } from '../../../mocks/luminaireAnalytics';
import { OPTIMAL_ANALYTICS, RADAR_AXES } from '../../../mocks/radarNormalization';
import {
  ComparisonMode,
  LuminaireAnalytics,
  MockLuminaire,
  MonthlyAnalyticsPoint,
  ProjectType,
} from '../../../types/analytics';
import { InsightsCard } from './InsightsCard';
import { LampThumb } from './LampThumb';
import { LuminairePickerModal } from './LuminairePickerModal';
import { MetricTable } from './MetricTable';
import { RadarChart } from './RadarChart';
import { ScenePreview } from './ScenePreview';
import { SparklineStrip } from './SparklineStrip';

// All radar / slot styling is built on the Taman green palette only.
// Slots are distinguished by treatment (filled vs outlined) rather than hue.
const SLOT_A_COLOR = '#B3EC49'; // green01 — solid fill on chips, filled polygon on radar
const SLOT_B_COLOR = '#B3EC49'; // green01 — outlined treatment (dashed stroke, no fill)
const OPTIMAL_COLOR = '#B3EC49';
const PORTFOLIO_COLOR = '#B3EC49';
const MARKET_COLOR = '#B3EC49';

type InnerTab = 'radar' | 'trends' | 'insights' | 'scene';

const INNER_TABS: { value: InnerTab; label: string; icon: typeof Activity; blurb: string }[] = [
  { value: 'radar', label: 'Radar Comparison', icon: Activity, blurb: 'Radar polygon + side-by-side metric table' },
  { value: 'trends', label: '12-month Trends', icon: LineChart, blurb: 'KPIs over time with last-4-month focus' },
  { value: 'insights', label: 'Auto Insights', icon: Sparkles, blurb: 'Competitive analysis & R&D gaps' },
  { value: 'scene', label: '3D Scene Preview', icon: BoxIcon, blurb: 'Side-by-side visual rendering' },
];

const COMPARISON_MODES: {
  value: ComparisonMode;
  label: string;
  sub: string;
  premium?: boolean;
}[] = [
  { value: 'product', label: 'vs Product', sub: 'Pick a second luminaire' },
  { value: 'optimal', label: 'vs Optimal', sub: 'TAMAN-generated ideal', premium: true },
  { value: 'portfolioAvg', label: 'vs Portfolio Avg', sub: 'Your catalog mean' },
  { value: 'marketAvg', label: 'vs Market Avg', sub: 'Cross-manufacturer mean', premium: true },
];

const buildPortfolioAverage = (luminaires: MockLuminaire[]): LuminaireAnalytics => {
  const used = luminaires.filter((l) => l.analytics.matchFrequencyTotal > 0);
  const n = Math.max(1, used.length);
  const avg = (sel: (l: MockLuminaire) => number) => used.reduce((acc, l) => acc + sel(l), 0) / n;
  const projectTypes = new Set<ProjectType>();
  const regions = new Set<string>();
  used.forEach((l) => {
    l.analytics.projectTypesCovered.forEach((t) => projectTypes.add(t));
    l.analytics.regionsActive.forEach((r) => regions.add(r));
  });
  const monthly: MonthlyAnalyticsPoint[] = [];
  const monthLen = used[0]?.analytics.monthlyTimeSeries.length ?? 12;
  for (let i = 0; i < monthLen; i++) {
    const monthLabel = used[0]?.analytics.monthlyTimeSeries[i]?.month ?? '';
    monthly.push({
      month: monthLabel,
      compatibilityScoreAvg: avg((l) => l.analytics.monthlyTimeSeries[i]?.compatibilityScoreAvg ?? 0),
      matchFrequencyTotal: avg((l) => l.analytics.monthlyTimeSeries[i]?.matchFrequencyTotal ?? 0),
      applyCount: avg((l) => l.analytics.monthlyTimeSeries[i]?.applyCount ?? 0),
      favoriteCount: avg((l) => l.analytics.monthlyTimeSeries[i]?.favoriteCount ?? 0),
      inquiriesGenerated: avg((l) => l.analytics.monthlyTimeSeries[i]?.inquiriesGenerated ?? 0),
    });
  }
  return {
    compatibilityScoreAvg: avg((l) => l.analytics.compatibilityScoreAvg),
    compatibilityScoreP90: avg((l) => l.analytics.compatibilityScoreP90),
    optimalGapPercent: avg((l) => l.analytics.optimalGapPercent),
    matchFrequencyTotal: avg((l) => l.analytics.matchFrequencyTotal),
    matchFrequencyPerMonth: avg((l) => l.analytics.matchFrequencyPerMonth),
    top3MatchCount: avg((l) => l.analytics.top3MatchCount),
    top100MatchCount: avg((l) => l.analytics.top100MatchCount),
    applyCount: avg((l) => l.analytics.applyCount),
    applyRatePercent: avg((l) => l.analytics.applyRatePercent),
    favoriteCount: avg((l) => l.analytics.favoriteCount),
    favoriteRatePercent: avg((l) => l.analytics.favoriteRatePercent),
    projectTypesCovered: Array.from(projectTypes),
    projectTypeCount: avg((l) => l.analytics.projectTypeCount),
    regionsActive: Array.from(regions),
    regionsCount: avg((l) => l.analytics.regionsCount),
    inquiriesGenerated: avg((l) => l.analytics.inquiriesGenerated),
    leadConversionRatePercent: avg((l) => l.analytics.leadConversionRatePercent),
    monthlyTimeSeries: monthly,
    lastMatchedAt: null,
    firstSeenAt: new Date().toISOString(),
  };
};

const scaleAnalytics = (base: LuminaireAnalytics, factor: number): LuminaireAnalytics => ({
  ...base,
  compatibilityScoreAvg: Math.min(100, base.compatibilityScoreAvg * factor),
  optimalGapPercent: Math.min(100, base.optimalGapPercent * (1 / factor)),
  matchFrequencyPerMonth: base.matchFrequencyPerMonth * factor,
  applyRatePercent: Math.min(100, base.applyRatePercent * factor),
  favoriteRatePercent: Math.min(100, base.favoriteRatePercent * factor),
  projectTypeCount: Math.min(10, base.projectTypeCount * factor),
  regionsCount: Math.min(20, base.regionsCount * factor),
  leadConversionRatePercent: Math.min(100, base.leadConversionRatePercent * factor),
});

// ── Left-column slot card — green-only treatment.
//   • Slot A = filled green (green bg, black letter)
//   • Slot B = outlined green (transparent bg, green border + green letter)
const SlotCard = ({
  letter,
  luminaire,
  label,
  subtitle,
  onChange,
  onClear,
  modeLocked,
}: {
  letter: 'A' | 'B';
  luminaire?: MockLuminaire | null;
  label: string | null;
  subtitle?: string;
  onChange: () => void;
  onClear: () => void;
  modeLocked?: boolean;
}) => {
  const isFilled = letter === 'A';
  return (
    <div
      className="rounded-xl border bg-blackP1/40 p-3 transition-colors"
      style={{ borderColor: label ? '#B3EC49' : '#313131' }}
    >
      <div className="flex items-start gap-3">
        {luminaire ? (
          <div className="relative flex-shrink-0">
            <LampThumb luminaire={luminaire} size={44} />
            <span
              className={`absolute -top-1.5 -left-1.5 w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold border-2 ${
                isFilled ? 'bg-green01 text-black border-green01' : 'bg-black text-green01 border-green01'
              }`}
            >
              {letter}
            </span>
          </div>
        ) : (
          <div
            className={`w-9 h-9 rounded-lg flex items-center justify-center font-bold text-sm flex-shrink-0 border-2 ${
              isFilled ? 'bg-green01 text-black border-green01' : 'bg-transparent text-green01 border-green01'
            }`}
          >
            {letter}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-greyP3 font-semibold">Slot {letter}</div>
          {label ? (
            <>
              <div className="text-sm text-white font-semibold leading-tight truncate" title={label}>
                {label}
              </div>
              {subtitle && <div className="text-[10px] text-greyP2 truncate mt-0.5">{subtitle}</div>}
              {luminaire && (
                <div className="text-[10px] text-greyP1 mt-1.5 flex flex-wrap gap-x-2">
                  <span>{luminaire.power}W</span>
                  <span className="text-greyP3">·</span>
                  <span>{luminaire.colorTemperature}K</span>
                  <span className="text-greyP3">·</span>
                  <span>{(luminaire.lumenOutput / luminaire.power).toFixed(0)} lm/W</span>
                </div>
              )}
            </>
          ) : (
            <div className="text-xs text-greyP2 italic mt-0.5">Not selected</div>
          )}
        </div>
      </div>

      {!modeLocked ? (
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={onChange}
            className="flex-1 text-[11px] font-semibold px-3 py-1.5 rounded-full border border-greyP4 bg-greyP5/60 text-white hover:bg-green01/10 hover:border-green01/60 hover:text-green01 transition-colors"
          >
            {label ? 'Change' : 'Pick luminaire'}
          </button>
          {label && (
            <button
              onClick={onClear}
              className="w-8 h-8 rounded-full hover:bg-greyP4 flex items-center justify-center text-greyP2 hover:text-white transition-colors"
              title="Clear slot"
            >
              <X size={14} />
            </button>
          )}
        </div>
      ) : (
        <div className="mt-3 text-[10px] uppercase tracking-wider text-greyP3 text-center py-1.5 rounded-full bg-greyP5/40">
          Locked by comparison mode
        </div>
      )}
    </div>
  );
};

// ── Comparison-mode row (appears only inside the Radar Comparison tab).
const ComparisonModePicker = ({
  mode,
  onChange,
  disabled,
}: {
  mode: ComparisonMode;
  onChange: (mode: ComparisonMode) => void;
  disabled: boolean;
}) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-greyP3 font-bold mb-2 px-1">Comparison mode</div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
      {COMPARISON_MODES.map((opt) => {
        const active = mode === opt.value;
        const locked = !!opt.premium;
        return (
          <button
            key={opt.value}
            onClick={() => !locked && onChange(opt.value)}
            disabled={disabled || locked}
            title={locked ? 'Available on premium plans — coming soon' : opt.sub}
            className={`relative text-left flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors disabled:cursor-not-allowed ${
              active
                ? 'bg-green01 text-black border-green01'
                : locked
                  ? 'bg-blackP1/40 text-greyP2 border-greyP4'
                  : 'bg-blackP1/40 text-white border-greyP4 hover:border-green01/60 hover:bg-green01/5 disabled:opacity-40'
            }`}
          >
            {/* Indicator dot — filled for A's peer in product mode, outlined otherwise */}
            <span
              className={`w-2.5 h-2.5 rounded-full flex-shrink-0 border-2 ${
                active
                  ? 'bg-black border-black'
                  : opt.value === 'product'
                    ? 'bg-green01 border-green01'
                    : 'bg-transparent border-green01'
              }`}
            />
            <div className="flex-1 min-w-0">
              <div className={`text-xs font-semibold ${active ? 'text-black' : 'text-white'}`}>{opt.label}</div>
              <div className={`text-[10px] truncate ${active ? 'text-black/60' : 'text-greyP2'}`}>{opt.sub}</div>
            </div>
            {locked && (
              <span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full border border-green01/50 text-green01 bg-green01/10 flex-shrink-0">
                <Crown size={9} />
                Premium
              </span>
            )}
          </button>
        );
      })}
    </div>
  </div>
);

interface LuminairePerformanceTabProps {
  /** When the user arrives via "Performance" deep-link from My Luminaires, the row id is pre-loaded into Slot A. */
  initialSlotAId?: string | null;
}

export const LuminairePerformanceTab = ({ initialSlotAId }: LuminairePerformanceTabProps = {}) => {
  const luminaires = MOCK_LUMINAIRES;
  const resolvedInitialA =
    initialSlotAId && luminaires.some((l) => l.id === initialSlotAId) ? initialSlotAId : (luminaires[0]?.id ?? null);
  const [slotAId, setSlotAId] = useState<string | null>(resolvedInitialA);
  const [slotBId, setSlotBId] = useState<string | null>(luminaires.find((l) => l.id !== resolvedInitialA)?.id ?? null);
  const [previousSlotBId, setPreviousSlotBId] = useState<string | null>(
    luminaires.find((l) => l.id !== resolvedInitialA)?.id ?? null,
  );
  const [mode, setMode] = useState<ComparisonMode>('product');
  const [innerTab, setInnerTab] = useState<InnerTab>('radar');
  const [pickerOpen, setPickerOpen] = useState<null | 'A' | 'B'>(null);
  const [animPhase, setAnimPhase] = useState<'loading' | 'ready'>('loading');

  useEffect(() => {
    const t = setTimeout(() => setAnimPhase('ready'), 600);
    return () => clearTimeout(t);
  }, []);

  const slotALum = useMemo(() => luminaires.find((l) => l.id === slotAId) ?? null, [luminaires, slotAId]);
  const slotBLum = useMemo(() => luminaires.find((l) => l.id === slotBId) ?? null, [luminaires, slotBId]);
  const portfolioAvg = useMemo(() => buildPortfolioAverage(luminaires), [luminaires]);
  const marketAvg = useMemo(() => scaleAnalytics(portfolioAvg, 0.85), [portfolioAvg]);

  // Portfolio mean efficacy (lm/W) — used as anchor for competitive insights
  const portfolioMeanEfficacy = useMemo(() => {
    const used = luminaires.filter((l) => l.analytics.matchFrequencyTotal > 0);
    if (!used.length) return 130;
    return used.reduce((acc, l) => acc + l.lumenOutput / l.power, 0) / used.length;
  }, [luminaires]);

  const effectiveSlotB = useMemo(() => {
    if (mode === 'optimal')
      return { id: '__optimal__', label: 'TAMAN Optimal', subtitle: 'All axes at 100', analytics: OPTIMAL_ANALYTICS };
    if (mode === 'portfolioAvg')
      return {
        id: '__portfolio__',
        label: 'Portfolio Average',
        subtitle: 'Your catalog mean',
        analytics: portfolioAvg,
      };
    if (mode === 'marketAvg')
      return { id: '__market__', label: 'Market Average', subtitle: 'Cross-manufacturer mean', analytics: marketAvg };
    if (slotBLum)
      return {
        id: slotBLum.id,
        label: slotBLum.name,
        subtitle: `${slotBLum.sku} · ${slotBLum.opticCode}`,
        analytics: slotBLum.analytics,
      };
    return null;
  }, [mode, slotBLum, portfolioAvg, marketAvg]);

  const slotBColor =
    mode === 'optimal'
      ? OPTIMAL_COLOR
      : mode === 'portfolioAvg'
        ? PORTFOLIO_COLOR
        : mode === 'marketAvg'
          ? MARKET_COLOR
          : SLOT_B_COLOR;
  const slotBStroke: 'solid' | 'dashed' = mode === 'product' ? 'solid' : 'dashed';

  const handleModeChange = useCallback(
    (next: ComparisonMode) => {
      // Optimal & Market Avg are premium-gated — guard at the handler too.
      if (next === 'optimal' || next === 'marketAvg') return;
      if (mode === 'product' && next !== 'product' && slotBId) setPreviousSlotBId(slotBId);
      if (next === 'product' && mode !== 'product') {
        setSlotBId(previousSlotBId);
      }
      setMode(next);
    },
    [mode, slotBId, previousSlotBId],
  );

  const handlePick = (l: MockLuminaire) => {
    if (pickerOpen === 'A') {
      if (l.id === slotBId && mode === 'product') {
        toast.warn("Can't compare a luminaire to itself.", {
          style: { background: '#232323', border: '1px solid #FF8A00', color: 'white' },
        });
        return;
      }
      setSlotAId(l.id);
    } else if (pickerOpen === 'B') {
      if (l.id === slotAId) {
        toast.warn("Can't compare a luminaire to itself.", {
          style: { background: '#232323', border: '1px solid #FF8A00', color: 'white' },
        });
        return;
      }
      setSlotBId(l.id);
      setPreviousSlotBId(l.id);
    }
  };

  const radarSlotA = slotALum
    ? { label: slotALum.name, analytics: slotALum.analytics, color: SLOT_A_COLOR, stroke: 'solid' as const }
    : null;
  const radarSlotB = effectiveSlotB
    ? { label: effectiveSlotB.label, analytics: effectiveSlotB.analytics, color: slotBColor, stroke: slotBStroke }
    : null;

  const hasUsage = slotALum?.analytics.matchFrequencyTotal !== 0;
  const isLoading = animPhase === 'loading';
  const modeLocksB = mode !== 'product';

  return (
    <div className="flex-1 flex flex-col xl:flex-row overflow-hidden bg-black">
      {/* ─── LEFT COLUMN — slot A / B (mode picker lives in the Radar tab) ──── */}
      <aside className="xl:w-[300px] xl:flex-shrink-0 xl:h-full overflow-y-auto border-b xl:border-b-0 xl:border-r border-greyP4 bg-blackP2/40 p-4 space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-greyP3 font-bold mb-2 px-1">
            Comparison subjects
          </div>
          <div className="space-y-3">
            <SlotCard
              letter="A"
              luminaire={slotALum}
              label={slotALum?.name ?? null}
              subtitle={slotALum ? `${slotALum.sku} · ${slotALum.opticCode}` : undefined}
              onChange={() => setPickerOpen('A')}
              onClear={() => setSlotAId(null)}
            />
            <SlotCard
              letter="B"
              luminaire={mode === 'product' ? slotBLum : null}
              label={effectiveSlotB?.label ?? null}
              subtitle={effectiveSlotB?.subtitle}
              onChange={() => setPickerOpen('B')}
              onClear={() => setSlotBId(null)}
              modeLocked={modeLocksB}
            />
          </div>
          <p className="mt-3 px-1 text-[10px] text-greyP3 leading-relaxed">
            <span className="inline-block w-2 h-2 rounded-full bg-green01 mr-1.5 align-middle" />
            Slot A renders filled ·{' '}
            <span className="inline-block w-2 h-2 rounded-full border border-green01 align-middle mr-1.5" />
            Slot B renders outlined on the radar.
          </p>
        </div>
      </aside>

      {/* ─── RIGHT COLUMN — inner sub-tabs + content ─────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Inner tab bar */}
        <div className="border-b border-greyP4 bg-black px-4 md:px-6 flex items-center gap-1 overflow-x-auto">
          {INNER_TABS.map((tab) => {
            const Icon = tab.icon;
            const active = innerTab === tab.value;
            return (
              <button
                key={tab.value}
                onClick={() => setInnerTab(tab.value)}
                title={tab.blurb}
                className={`relative flex items-center gap-2 px-3 md:px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                  active ? 'text-green01' : 'text-greyP1 hover:text-white'
                }`}
              >
                <Icon size={14} className={active ? 'text-green01' : 'text-greyP3'} />
                {tab.label}
                {active && <span className="absolute left-2 right-2 -bottom-px h-0.5 bg-green01 rounded-t" />}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto">
          {!slotALum ? (
            <div className="p-10 flex items-center justify-center min-h-[50vh]">
              <div className="text-center max-w-md">
                <div className="w-16 h-16 mx-auto rounded-full bg-greyP5 border border-greyP4 flex items-center justify-center mb-4">
                  <Sparkles size={28} className="text-greyP3" />
                </div>
                <h3 className="text-white text-lg font-semibold mb-1">Pick a luminaire to begin</h3>
                <p className="text-sm text-greyP2 mb-4">
                  Choose the primary luminaire for Slot A from your catalog. Then optionally compare it against a second
                  product, the optimal distribution, your portfolio mean, or the market average.
                </p>
                <button
                  onClick={() => setPickerOpen('A')}
                  className="px-5 py-2 rounded-full bg-white text-black font-semibold text-sm hover:bg-gray-200 transition-colors"
                >
                  Pick Slot A
                </button>
              </div>
            </div>
          ) : (
            <div className="p-4 md:p-6">
              {!hasUsage && (
                <div className="rounded-lg border border-greyP3/40 bg-greyP5/40 px-4 py-3 text-xs text-greyP1 mb-4">
                  This luminaire has no recent usage data. Metrics shown are historical baselines or zero.
                </div>
              )}

              {innerTab === 'radar' && (
                <SectionShell
                  title="Radar Comparison"
                  hint="The 8-axis performance polygon with raw numeric values side-by-side. Hover any vertex for tooltip details. Choose what Slot B represents below."
                >
                  <ComparisonModePicker mode={mode} onChange={handleModeChange} disabled={!slotALum} />

                  {/* Two-column on xl: radar left, metrics right.
                      Stacked on macbook-sized screens and below: radar on top, metrics underneath. */}
                  <div className="grid grid-cols-1 2xl:grid-cols-[minmax(0,640px)_minmax(0,1fr)] gap-6 mt-5">
                    <div className="flex justify-center">
                      <div className={isLoading ? 'opacity-40 animate-pulse' : ''}>
                        <RadarChart
                          slotA={radarSlotA}
                          slotB={radarSlotB}
                          size={560}
                          onAxisClick={() =>
                            toast.info('Drill-down view coming soon.', {
                              style: { background: '#232323', border: '1px solid #B3EC49', color: 'white' },
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <MetricTable
                        slotA={
                          slotALum ? { label: slotALum.name, color: SLOT_A_COLOR, analytics: slotALum.analytics } : null
                        }
                        slotB={
                          effectiveSlotB
                            ? { label: effectiveSlotB.label, color: slotBColor, analytics: effectiveSlotB.analytics }
                            : null
                        }
                      />
                      <div className="text-[11px] text-greyP3 px-1">
                        {RADAR_AXES.length} axes · "Optimal Gap" is inverted on the radar (shown as 100 − gap) but raw
                        here.
                      </div>
                    </div>
                  </div>
                </SectionShell>
              )}

              {innerTab === 'trends' && (
                <SectionShell
                  title="12-month Trends"
                  hint="Each chart spans the last 12 months. The shaded region on the right highlights the last 4 months; the % delta in the top-right of each card is the trailing-4-month change."
                >
                  <SparklineStrip
                    primary={{ analytics: slotALum.analytics, color: SLOT_A_COLOR, label: slotALum.name }}
                    secondary={
                      effectiveSlotB
                        ? { analytics: effectiveSlotB.analytics, color: slotBColor, label: effectiveSlotB.label }
                        : undefined
                    }
                  />
                </SectionShell>
              )}

              {innerTab === 'insights' && (
                <SectionShell
                  title="Auto Insights"
                  hint="Competitive analysis derived from the comparison and class benchmarks. In production this would be generated by a backend rule engine with cross-manufacturer access."
                >
                  <InsightsCard
                    slotA={slotALum}
                    slotB={mode === 'product' ? slotBLum : null}
                    portfolioMeanEfficacy={portfolioMeanEfficacy}
                  />
                </SectionShell>
              )}

              {innerTab === 'scene' && (
                <SectionShell
                  title="3D Scene Preview"
                  hint="Side-by-side visual preview from identical camera positions. Spot colour-temperature impact, beam shape, and intensity differences before specifying."
                >
                  <ScenePreview primary={slotALum} secondary={mode === 'product' ? slotBLum : null} />
                </SectionShell>
              )}
            </div>
          )}
        </div>
      </div>

      <LuminairePickerModal
        open={pickerOpen !== null}
        onClose={() => setPickerOpen(null)}
        luminaires={luminaires}
        slotAId={slotAId}
        slotBId={slotBId}
        targetSlot={pickerOpen ?? 'A'}
        onPick={handlePick}
      />
    </div>
  );
};

const SectionShell = ({ title, hint, children }: { title: string; hint: string; children: React.ReactNode }) => (
  <section className="space-y-4">
    <div>
      <h2 className="text-white text-lg font-bold">{title}</h2>
      <p className="text-greyP2 text-xs mt-0.5 max-w-3xl">{hint}</p>
    </div>
    <div className="rounded-2xl border border-greyP4 bg-blackP1/40 p-4 md:p-6">{children}</div>
  </section>
);
